/**
 * Iter XX.274 — Founding 100 · post-purchase welcome page.
 *
 * The Stripe success URL lands here with `?session_id=cs_...`.
 * We validate against the backend (which requires the session
 * to be `paid`), then render the "You're in" state and collect
 * optional profile info. Never claims success without server
 * verification.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import PageErrorBoundary from "../components/PageErrorBoundary";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const ROLES = ["Batter", "Bowler", "Wicket-keeper", "All-rounder", "Coach", "Fan"];

export default function Founding100Welcome() {
  return (
    <PageErrorBoundary pageName="Founding100Welcome">
      <Inner />
    </PageErrorBoundary>
  );
}

function Inner() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [instagram, setInstagram] = useState("");
  const [role, setRole] = useState("");
  const [country, setCountry] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!sessionId) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get("/founding100/row", {
          params: { session_id: sessionId },
        });
        setRow(data);
        setEmail(data?.email || "");
        setInstagram(data?.instagram || "");
        setRole(data?.role || "");
        setCountry(data?.country || "");
        // NOTE: the server-side Stripe-signature-verified webhook
        // (see /app/backend/founding100.py::mark_paid_from_webhook)
        // is the SOLE authoritative emitter of the funnel success
        // event for this checkout. This page must NEVER emit that
        // event from the client — a client emit would duplicate
        // the funnel row per checkout and on every revisit of
        // this URL.
      } catch (e) {
        // 404 = unknown, 409 = unpaid — either way, do NOT claim success.
        setRow(null);
      }
      setLoading(false);
    })();
  }, [sessionId]);

  const onSave = async () => {
    if (saving || !sessionId) return;
    setSaving(true);
    try {
      await api.post("/founding100/profile", {
        session_id: sessionId,
        email: email || null,
        instagram: instagram || null,
        role: role || null,
        country: country || null,
      });
      setSaved(true);
      toast.success("Saved. We’ll be in touch.");
    } catch {
      toast.error("Couldn’t save — try again.");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-neutral-950 text-neutral-200 p-6">Loading…</div>;
  }

  if (!row) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-100 p-6">
        <div className="max-w-lg mx-auto pt-16">
          <h1 className="text-2xl font-semibold text-neutral-50 mb-3">
            Payment not verified yet
          </h1>
          <p className="text-sm text-neutral-400 leading-relaxed">
            We can’t confirm this Founding 100 session as paid.
            If you just completed checkout, refresh in a moment.
            Otherwise, {" "}
            <Link to="/founding100" className="text-emerald-300 underline">
              return to Founding 100
            </Link>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="max-w-lg mx-auto px-6 pt-16 pb-24">
        <div className="flex items-center gap-2 mb-6">
          <CheckCircle2 size={22} className="text-emerald-400" />
          <h1 className="text-2xl font-semibold text-neutral-50" data-testid="founding100-welcome-heading">
            You’re in. Welcome to the Founding 100.
          </h1>
        </div>
        <p className="text-sm text-neutral-300 leading-relaxed mb-8">
          You’ll be among the first cricketers helping shape Gully Pro.
          We’ll contact you with next steps, early-access updates, and
          founding-member benefits.
        </p>

        <div className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-5 space-y-4" data-testid="founding100-profile-form">
          <Field label="Email" value={email} onChange={setEmail}
                    placeholder="you@example.com" type="email"
                    testId="founding100-email" />
          <Field label="Instagram (optional)" value={instagram}
                    onChange={setInstagram} placeholder="@yourhandle"
                    testId="founding100-instagram" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 mb-2">
              Cricket role
            </div>
            <div className="flex flex-wrap gap-2">
              {ROLES.map((r) => (
                <button key={r}
                  onClick={() => setRole(r)}
                  data-testid={`founding100-role-${r.toLowerCase().replace(/[^a-z]/g, "_")}`}
                  className={
                    "text-xs px-3 py-1.5 rounded-full border transition-colors "
                    + (role === r
                      ? "border-emerald-500 bg-emerald-500/10 text-emerald-200"
                      : "border-neutral-700 text-neutral-300 hover:border-neutral-500")
                  }>
                  {r}
                </button>
              ))}
            </div>
          </div>
          <Field label="Country" value={country} onChange={setCountry}
                    placeholder="India, United Kingdom…"
                    testId="founding100-country" />
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="founding100-save"
            className={
              "w-full inline-flex items-center justify-center gap-2 px-4 "
              + "py-3 rounded-xl bg-emerald-500 text-neutral-950 "
              + "font-medium hover:bg-emerald-400 transition-colors "
              + "disabled:opacity-50"
            }>
            {saved ? "Saved" : "Save details"}
          </button>
        </div>

        {row.granted_plus && (
          <div className="mt-6 text-xs text-neutral-500" data-testid="founding100-plus-confirmed">
            12 months of Gully Plus is on your account.
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 mb-2">
        {label}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={type}
        data-testid={testId}
        className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-600"
      />
    </div>
  );
}
